import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_ORIGIN } from '../apiMode';
import { getLiveTalkRoomTitle } from '../data/talkRoomsConfig';
import {
  IconMic,
  IconMicMuted,
  IconHeadphones,
  IconHandRaised,
  IconLeaveRoom,
} from '../components/talkAppleIcons';
import ProfileOverlayModal from '../components/ProfileOverlayModal';
import ReportModal from '../components/ReportModal';
import IapSubscriptionDetails from '../components/IapSubscriptionDetails';
import {
  isIosNativeCheckout,
  purchasePremium,
  loadIapPremiumProduct,
} from '../utils/checkout';
import { useAuth } from '../context/AuthContext';

const ICE = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };

function safeRoomId(id) {
  return String(id || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48);
}

export default function TalkVoiceRoom() {
  const { roomId: rawId } = useParams();
  const roomId = safeRoomId(rawId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremiumUser = Number(user?.talk_premium) === 1;
  const FREE_LIMIT_SEC = 60 * 60;
  const [timeLeft, setTimeLeft] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallBusy, setPaywallBusy] = useState(false);
  const [paywallErr, setPaywallErr] = useState('');
  const [iapProduct, setIapProduct] = useState(null);
  const [iapLoadErr, setIapLoadErr] = useState('');
  const [iapRetryKey, setIapRetryKey] = useState(0);
  const { refreshMe } = useAuth();

  const [participants, setParticipants] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(true);
  const [roomTitle, setRoomTitle] = useState('');
  const [hostSocketId, setHostSocketId] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [canSpeak, setCanSpeak] = useState(true);
  const [speakRequests, setSpeakRequests] = useState([]);
  const [requestedSpeak, setRequestedSpeak] = useState(false);
  const selfSocketIdRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatMessagesScrollRef = useRef(null);
  /** @type {null | { type: 'user'; userId: string } | { type: 'demo'; name: string }} */
  const [profileOverlay, setProfileOverlay] = useState(null);
  // Guideline 1.2: ユーザー作成ルームの通報
  const [customRoom, setCustomRoom] = useState(null);
  const [safetyMenu, setSafetyMenu] = useState(false);
  const [reportRoomOpen, setReportRoomOpen] = useState(false);
  const roomReportedRef = useRef(false);

  const openProfileInRoom = useCallback((uid) => {
    if (uid == null || uid === '') return;
    setProfileOverlay({ type: 'user', userId: String(uid) });
  }, []);

  const [volumeMap, setVolumeMap] = useState({});

  const localStreamRef = useRef(null);
  const socketRef = useRef(null);
  const pcMapRef = useRef(new Map());
  const remoteAudioParentRef = useRef(null);
  const remoteAudioElsRef = useRef(new Map());
  const audioContextRef = useRef(null);
  const analyserMapRef = useRef(new Map());
  const participantsRef = useRef([]);

  const removeRemoteAudio = useCallback((socketId) => {
    const el = remoteAudioElsRef.current.get(socketId);
    if (el) {
      el.srcObject = null;
      el.remove();
      remoteAudioElsRef.current.delete(socketId);
    }
    analyserMapRef.current.delete(socketId);
  }, []);

  const attachRemoteAudio = useCallback((socketId, stream) => {
    const parent = remoteAudioParentRef.current;
    if (!parent) return;
    let el = remoteAudioElsRef.current.get(socketId);
    if (!el) {
      el = document.createElement('audio');
      el.autoplay = true;
      el.playsInline = true;
      el.setAttribute('data-peer', socketId);
      parent.appendChild(el);
      remoteAudioElsRef.current.set(socketId, el);
    }
    el.srcObject = stream;
    const ctx = audioContextRef.current;
    if (ctx && !analyserMapRef.current.has(socketId)) {
      try {
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        src.connect(analyser);
        analyserMapRef.current.set(socketId, analyser);
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    setRoomTitle(getLiveTalkRoomTitle(roomId));
  }, [roomId]);

  // ユーザーが作ったルームなら、ルーム自体を通報できるようにする（Guideline 1.2）
  useEffect(() => {
    let alive = true;
    axios.get('/api/rooms')
      .then(({ data }) => {
        if (!alive || !Array.isArray(data)) return;
        const found = data.find(r => String(r.id) === String(roomId));
        if (found) {
          setCustomRoom(found);
          setRoomTitle(found.title);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [roomId]);

  useEffect(() => {
    if (!localStreamRef.current) return;
    const t = localStreamRef.current.getAudioTracks()[0];
    if (t) t.enabled = canSpeak && micOn;
  }, [canSpeak, micOn]);

  useEffect(() => {
    if (!roomId) {
      navigate('/talk', { replace: true });
      return undefined;
    }
    let cancelled = false;
    const pcMap = new Map();
    pcMapRef.current = pcMap;

    const createOfferToPeer = async (remoteSocketId, stream, socket) => {
      if (pcMap.has(remoteSocketId)) return;
      const pc = new RTCPeerConnection(ICE);
      pcMap.set(remoteSocketId, pc);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          socket.emit('talk:ice', { to: remoteSocketId, candidate: e.candidate, roomId });
        }
      };
      pc.ontrack = (e) => {
        const [remoteStream] = e.streams;
        if (remoteStream) attachRemoteAudio(remoteSocketId, remoteStream);
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('talk:offer', { to: remoteSocketId, sdp: pc.localDescription, roomId });
    };

    const handleOffer = async (from, sdp, stream, socket) => {
      if (pcMap.has(from)) return;
      const pc = new RTCPeerConnection(ICE);
      pcMap.set(from, pc);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => {
        if (e.candidate) socket.emit('talk:ice', { to: from, candidate: e.candidate, roomId });
      };
      pc.ontrack = (e) => {
        const [remoteStream] = e.streams;
        if (remoteStream) attachRemoteAudio(from, remoteStream);
      };
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('talk:answer', { to: from, sdp: pc.localDescription, roomId });
    };

    const handleAnswer = async (from, sdp) => {
      const pc = pcMap.get(from);
      if (!pc) return;
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
    };

    const handleIce = async (from, candidate) => {
      const pc = pcMap.get(from);
      if (!pc || !candidate) return;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {
        /* ignore */
      }
    };

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
          video: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        localStreamRef.current = stream;

        try {
          const ctx = new AudioContext();
          audioContextRef.current = ctx;
          const selfSrc = ctx.createMediaStreamSource(stream);
          const selfAnalyser = ctx.createAnalyser();
          selfAnalyser.fftSize = 256;
          selfAnalyser.smoothingTimeConstant = 0.3;
          selfSrc.connect(selfAnalyser);
          analyserMapRef.current.set('self', selfAnalyser);
        } catch { /* ignore */ }

        const socket = io(API_ORIGIN, { transports: ['websocket', 'polling'] });
        socketRef.current = socket;

        socket.on('connect', () => {
          socket.emit('talk:join', { roomId, userId: user.id, username: user.username || 'User' });
        });

        socket.on('talk:joinDenied', ({ reason }) => {
          if (cancelled) return;
          navigate('/talk', {
            replace: true,
            state: { talkDeniedReason: reason || '入室できませんでした' },
          });
        });

        socket.on('talk:joined', ({
          peers,
          selfSocketId,
          hostSocketId: hs,
          isHost: hostFlag,
          canSpeak: cs,
        }) => {
          if (cancelled) return;
          setConnecting(false);
          selfSocketIdRef.current = selfSocketId;
          setHostSocketId(hs);
          setIsHost(!!hostFlag);
          setCanSpeak(!!cs);
          setSpeakRequests([]);
          setRequestedSpeak(false);
          const self = {
            socketId: selfSocketId,
            userId: user.id,
            username: user.username || 'あなた',
            isSelf: true,
            isHost: !!hostFlag,
            canSpeak: !!cs,
          };
          setParticipants([
            self,
            ...peers.map((p) => ({
              ...p,
              isSelf: false,
              isHost: !!p.isHost,
              canSpeak: p.canSpeak !== false,
            })),
          ]);
        });

        socket.on('talk:speakRequest', (msg) => {
          if (cancelled) return;
          setSpeakRequests((prev) => {
            if (prev.some((r) => r.fromSocketId === msg.fromSocketId)) return prev;
            return [...prev, {
              fromSocketId: msg.fromSocketId,
              userId: msg.userId,
              username: msg.username,
            }];
          });
        });

        socket.on('talk:speakApproved', () => {
          if (cancelled) return;
          setCanSpeak(true);
          setRequestedSpeak(false);
          setParticipants((prev) => prev.map((p) => (p.isSelf ? { ...p, canSpeak: true } : p)));
        });

        socket.on('talk:speakRejected', () => {
          setRequestedSpeak(false);
        });

        socket.on('talk:peerCanSpeak', ({ socketId }) => {
          if (cancelled) return;
          if (selfSocketIdRef.current && socketId === selfSocketIdRef.current) {
            setCanSpeak(true);
          }
          setParticipants((prev) => prev.map((p) => (
            p.socketId === socketId ? { ...p, canSpeak: true } : p
          )));
          setSpeakRequests((prev) => prev.filter((r) => r.fromSocketId !== socketId));
        });

        socket.on('talk:peerCannotSpeak', ({ socketId }) => {
          if (cancelled) return;
          if (selfSocketIdRef.current && socketId === selfSocketIdRef.current) {
            setCanSpeak(false);
          }
          setParticipants((prev) => prev.map((p) => (
            p.socketId === socketId ? { ...p, canSpeak: false } : p
          )));
        });

        socket.on('talk:hostChanged', ({ hostSocketId: newHost }) => {
          if (cancelled) return;
          setHostSocketId(newHost);
          const sid = selfSocketIdRef.current;
          if (sid && newHost === sid) {
            setIsHost(true);
            setCanSpeak(true);
          } else if (sid) {
            setIsHost(false);
          }
          setParticipants((prev) => prev.map((p) => {
            if (p.socketId === newHost) {
              return { ...p, isHost: true, canSpeak: true };
            }
            return { ...p, isHost: false };
          }));
        });

        socket.on('talk:peerJoined', async ({ peer }) => {
          if (cancelled) return;
          setParticipants((prev) => {
            if (prev.some((p) => p.socketId === peer.socketId)) return prev;
            return [...prev, {
              ...peer,
              isSelf: false,
              isHost: !!peer.isHost,
              canSpeak: peer.canSpeak !== false,
            }];
          });
          try {
            await createOfferToPeer(peer.socketId, stream, socket);
          } catch (e) {
            console.error('[talk offer]', e);
          }
        });

        socket.on('talk:offer', async ({ from, sdp }) => {
          if (cancelled) return;
          try {
            await handleOffer(from, sdp, stream, socket);
          } catch (e) {
            console.error('[talk answer flow]', e);
          }
        });

        socket.on('talk:answer', async ({ from, sdp }) => {
          if (cancelled) return;
          try {
            await handleAnswer(from, sdp);
          } catch (e) {
            console.error('[talk set answer]', e);
          }
        });

        socket.on('talk:ice', async ({ from, candidate }) => {
          if (cancelled) return;
          await handleIce(from, candidate);
        });

        socket.on('talk:peerLeft', ({ socketId }) => {
          const pc = pcMap.get(socketId);
          if (pc) {
            pc.close();
            pcMap.delete(socketId);
          }
          removeRemoteAudio(socketId);
          setParticipants((prev) => prev.filter((p) => p.socketId !== socketId));
          setSpeakRequests((prev) => prev.filter((r) => r.fromSocketId !== socketId));
        });

        socket.on('talk:peerMicToggle', ({ socketId, micOn: peerMicOn }) => {
          if (cancelled) return;
          setParticipants((prev) => prev.map((p) =>
            p.socketId === socketId ? { ...p, micOn: peerMicOn } : p
          ));
        });

        socket.on('talk:chatMsg', (msg) => {
          if (cancelled) return;
          setChatMessages((prev) => [...prev, msg]);
        });
      } catch (e) {
        if (!cancelled) {
          setError('マイクへのアクセスが必要です。ブラウザの設定を確認してください。');
          setConnecting(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      pcMap.forEach((pc) => pc.close());
      pcMap.clear();
      remoteAudioElsRef.current.forEach((el) => {
        el.srcObject = null;
        el.remove();
      });
      remoteAudioElsRef.current.clear();
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
      analyserMapRef.current.clear();
      audioContextRef.current?.close();
      audioContextRef.current = null;
      socketRef.current?.emit('talk:leave');
      socketRef.current?.disconnect();
      socketRef.current = null;
      setParticipants([]);
      setSpeakRequests([]);
      setRequestedSpeak(false);
      setHostSocketId(null);
      setIsHost(false);
      setCanSpeak(true);
      selfSocketIdRef.current = null;
    };
  }, [roomId, user.id, user.username, navigate, attachRemoteAudio, removeRemoteAudio]);

  // 無料ユーザー: ライブルーム接続完了後に1時間タイマー開始（プレミアムは対象外）
  useEffect(() => {
    if (isPremiumUser || connecting) return;
    setTimeLeft(FREE_LIMIT_SEC);
  }, [isPremiumUser, connecting, FREE_LIMIT_SEC]);

  useEffect(() => {
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      setShowPaywall(true);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, navigate]);

  // Keep the in-room paywall compliant with the main Premium screen. Product
  // data is loaded only when the paywall is visible and the purchase action
  // remains disabled until StoreKit returns the localized product details.
  useEffect(() => {
    if (!showPaywall || !isIosNativeCheckout() || iapProduct) return undefined;
    let cancelled = false;
    setIapLoadErr('');
    loadIapPremiumProduct()
      .then((product) => {
        if (!cancelled) setIapProduct(product);
      })
      .catch(() => {
        if (!cancelled) setIapLoadErr('ただいま App Store に接続できません');
      });
    return () => { cancelled = true; };
  }, [showPaywall, iapProduct, iapRetryKey]);

  const retryPaywallProduct = () => {
    setIapLoadErr('');
    setIapRetryKey((key) => key + 1);
  };

  const handlePaywallPurchase = async () => {
    if (isIosNativeCheckout() && !iapProduct) return;
    setPaywallErr('');
    setPaywallBusy(true);
    try {
      await purchasePremium({ refreshMe });
      setShowPaywall(false);
    } catch (e) {
      if (!e?.cancelled) setPaywallErr(e?.message || '購入に失敗しました');
    } finally {
      setPaywallBusy(false);
    }
  };

  const formatTimeLeft = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const toggleMic = () => {
    if (!canSpeak) return;
    const next = !micOn;
    setMicOn(next);
    socketRef.current?.emit('talk:micToggle', { roomId, micOn: next });
  };

  const leave = () => {
    navigate('/talk');
  };

  const requestRealSpeak = () => {
    socketRef.current?.emit('talk:requestSpeak', { roomId });
    setRequestedSpeak(true);
  };

  const approveRealSpeak = (fromSocketId) => {
    socketRef.current?.emit('talk:approveSpeak', { roomId, targetSocketId: fromSocketId });
    setSpeakRequests((prev) => prev.filter((r) => r.fromSocketId !== fromSocketId));
  };

  const rejectRealSpeak = (fromSocketId) => {
    socketRef.current?.emit('talk:rejectSpeak', { roomId, targetSocketId: fromSocketId });
    setSpeakRequests((prev) => prev.filter((r) => r.fromSocketId !== fromSocketId));
  };

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    socketRef.current?.emit('talk:chat', { roomId, text });
    setChatInput('');
  };

  // 新メッセージでメッセージ一覧のみ末尾へ（scrollIntoView は window まで伝播しうる）
  React.useEffect(() => {
    const el = chatMessagesScrollRef.current;
    if (!el) return;
    const t = window.setTimeout(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [chatMessages.length]);

  useEffect(() => { participantsRef.current = participants; }, [participants]);

  // Volume polling for real WebRTC streams (~12fps)
  useEffect(() => {
    const buf = new Uint8Array(128);
    const id = setInterval(() => {
      if (analyserMapRef.current.size === 0) return;
      const next = {};
      analyserMapRef.current.forEach((analyser, key) => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        next[key] = Math.sqrt(sum / buf.length);
      });
      setVolumeMap(next);
    }, 80);
    return () => clearInterval(id);
  }, []);

  const resumeSpeakHost = () => {
    if (!isHost || canSpeak) return;
    socketRef.current?.emit('talk:resumeSpeak', { roomId });
  };

  const profileUserIdForParticipant = useCallback((p) => {
    if (p.isSelf) return user?.id || null;
    const uid = p.userId;
    if (uid == null || uid === '') return null;
    return uid;
  }, [user?.id]);

  const openParticipantProfile = useCallback((e, p) => {
    e.stopPropagation();
    const id = profileUserIdForParticipant(p);
    if (id) openProfileInRoom(id);
  }, [openProfileInRoom, profileUserIdForParticipant]);

  const nSpeakers = participants.filter((p) => p.canSpeak !== false).length;
  const nListenersRoster = participants.filter((p) => p.canSpeak === false).length;
  const speakers = participants.filter((p) => p.canSpeak !== false);
  const listeners = participants.filter((p) => p.canSpeak === false);

  return (
    <div data-control-theme="dark" style={S.page}>
      <header style={S.header}>
        <span style={S.headerSpacer} />
        <div style={S.headerMid}>
          <h1 style={S.title}>{roomTitle}</h1>
          <span style={S.roomId}>{roomId}</span>
        </div>
        {/* ── 安全メニュー（Guideline 1.2） ── */}
        <div style={S.safetyWrap}>
          <button data-control="icon" data-control-id="TalkVoiceRoom-headerSafetyBtn"
            type="button"
            style={S.headerSafetyBtn}
            onClick={() => setSafetyMenu(v => !v)}
            title="通報・安全"
            aria-label="通報・安全メニュー"
            aria-expanded={safetyMenu}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.9" />
              <circle cx="12" cy="12" r="1.9" />
              <circle cx="19" cy="12" r="1.9" />
            </svg>
          </button>
          {safetyMenu && (
            <>
              <div style={S.safetyBackdrop} onClick={() => setSafetyMenu(false)} />
              <div style={S.safetyMenu} role="menu">
                {customRoom && String(customRoom.owner_id) !== String(user?.id) && (
                  <button data-control="row" data-control-id="TalkVoiceRoom-safetyMenuItem"
                    type="button"
                    style={S.safetyMenuItem}
                    role="menuitem"
                    onClick={() => { setSafetyMenu(false); setReportRoomOpen(true); }}
                  >
                    このルームを通報する
                  </button>
                )}
                <p style={S.safetyMenuHint}>
                  参加者の名前やアイコンをタップすると、その人を通報・ブロックできます。
                  違反が確認された場合は24時間以内に対応します。
                </p>
              </div>
            </>
          )}
        </div>
        <button data-control="quiet" data-control-id="TalkVoiceRoom-headerLeaveBtn" type="button" style={S.headerLeaveBtn} onClick={leave} title="退室" aria-label="退室">
          <IconLeaveRoom size={20} />
        </button>
      </header>

      <div ref={remoteAudioParentRef} style={{ display: 'none' }} aria-hidden />

      {error ? (
        <div style={S.errorBox}>
          <p style={S.errorText}>{error}</p>
          <button data-control="secondary" data-control-id="TalkVoiceRoom-leaveBtn" type="button" style={S.leaveBtn} onClick={leave}>一覧に戻る</button>
        </div>
      ) : (
        <div style={S.roomBody}>
          <div className="hide-scrollbar" style={S.participantStrip}>
          <div style={S.status}>
            {connecting ? (
              <p style={S.statusTxt}>接続中… マイクを許可してください</p>
            ) : (
              <>
                <p style={S.statusTxt}>
                  <span style={S.liveDot} aria-hidden />
                  <span style={S.statusLabel}>参加中</span>
                </p>
                {canSpeak && !micOn && (
                  <div style={S.statusMuted} role="status">
                    <span style={S.statusMutedIcon}>
                      <IconMicMuted size={20} />
                    </span>
                    <span>マイクをオフにしています。相手には声が届きません。</span>
                  </div>
                )}
                {timeLeft !== null && (
                  <div
                    style={{
                      ...S.statusMuted,
                      ...(timeLeft <= 600 ? S.statusMutedWarn : {}),
                    }}
                    role="status"
                  >
                    <span>⏱ 残り {formatTimeLeft(timeLeft)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          <section style={S.speakerStage}>
            <div style={S.stageHeader}>
              <div style={S.stageGlow} aria-hidden>
                <span style={S.stageOrb} />
                <span style={S.stageOrbSmall} />
              </div>
              <div style={S.stageStats} aria-label={`発言者 ${nSpeakers}人、聴講者 ${nListenersRoster}人`}>
                <span style={S.stageStatPill}>🎙 {nSpeakers}</span>
                <span style={S.stageStatPill}>🎧 {nListenersRoster}</span>
              </div>
            </div>

            <div style={S.grid}>
            {speakers.length === 0 && (
              <div style={S.emptySeatRow} aria-hidden>
                <span style={S.emptySeat} />
                <span style={{ ...S.emptySeat, ...S.emptySeatMain }} />
                <span style={S.emptySeat} />
              </div>
            )}
            {speakers.map((p) => {
              const canP = p.canSpeak !== false;
              const peerMicOff = !p.isSelf && canP && p.micOn === false;
              const selfMicOff = p.isSelf && canP && !micOn;
              const isMicOff = selfMicOff || peerMicOff;
              const vol = volumeMap[p.isSelf ? 'self' : p.socketId] || 0;
              const speaking = vol > 0.015;
              const ringSize = speaking ? Math.round(1 + vol * 22) : 0;
              const ringAlpha = speaking ? Math.min(0.75, vol * 9) : 0;
              const avatarBlock = (
                <div style={S.tileAvatarWrap}>
                  <span style={{
                    ...S.tileAvatar,
                    boxShadow: speaking
                      ? `0 0 0 ${ringSize}px rgba(201,168,76,${ringAlpha}), 0 0 0 ${ringSize + 5}px rgba(232,208,138,${ringAlpha * 0.3}), 0 0 ${ringSize + 10}px rgba(201,168,76,${ringAlpha * 0.5})`
                      : `0 0 0 1px rgba(201,168,76,0.2)`,
                    transition: 'box-shadow 0.1s ease',
                  }}>{(p.username || '?')[0].toUpperCase()}</span>
                  {isMicOff && (
                    <span style={S.micOffOverlay} aria-hidden>
                      <IconMicMuted size={13} />
                    </span>
                  )}
                </div>
              );
              return (
                <div
                  key={p.socketId}
                  style={{
                    ...S.tile,
                    ...(p.isHost ? S.tileHost : {}),
                    ...(p.isSelf ? S.tileSelf : {}),
                    ...(speaking ? S.tileSpeaking : {}),
                  }}
                >
                  <button data-control="media" data-control-id="TalkVoiceRoom-tileAvatarBtn"
                    type="button"
                    style={S.tileAvatarBtn}
                    onClick={(e) => openParticipantProfile(e, p)}
                    aria-label={`${p.username}のプロフィールを開く`}
                    title="プロフィール"
                  >
                    {avatarBlock}
                  </button>
                  <span style={S.tileName}>{p.isSelf ? '自分' : (p.username || '?')}</span>
                  <span style={S.roleRow}>
                    {p.isHost && <span style={S.badgeHost} title="ホスト" aria-label="ホスト">♛</span>}
                    {speaking && !isMicOff && <span style={S.badgeSpeaking} title="発話中" aria-label="発話中" />}
                    {isMicOff && <span style={S.badgeMicOff} title="ミュート" aria-label="ミュート"><IconMicMuted size={10} /></span>}
                  </span>
                </div>
              );
            })}
          </div>
          </section>

          <section style={S.listenerDeck}>
            <div style={S.listenerHead}>
              <span style={S.listenerWave} aria-hidden />
              <span style={S.listenerCount} aria-label={`聴講者 ${nListenersRoster}人`}>🎧 {nListenersRoster}</span>
            </div>
            {listeners.length > 0 ? (
              <div style={S.listenerGrid}>
                {listeners.map((p) => {
                  const profileId = profileUserIdForParticipant(p);
                  const initial = ((p.username || '?').trim().charAt(0) || '?').toUpperCase();
                  const displayName = p.isSelf ? '自分' : (p.username || '?');
                  const content = (
                    <>
                      <span style={{ ...S.listenerAvatar, ...(p.isSelf ? S.listenerAvatarSelf : {}) }}>
                        {initial}
                      </span>
                      <span style={S.listenerName}>{displayName}</span>
                      {p.isSelf && <span style={S.listenerSelfPill}>あなた</span>}
                    </>
                  );
                  if (profileId) {
                    return (
                      <button data-control="media" data-control-id="TalkVoiceRoom-listenerChip"
                        type="button"
                        key={p.socketId}
                        style={{ ...S.listenerChip, ...S.listenerChipBtn }}
                        onClick={() => openProfileInRoom(profileId)}
                        title={`${p.username || '?'}のプロフィール`}
                      >
                        {content}
                      </button>
                    );
                  }
                  return (
                    <div key={p.socketId} style={S.listenerChip}>
                      {content}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={S.listenerEmptySeats} aria-hidden>
                <span style={S.listenerEmptySeat} />
                <span style={S.listenerEmptySeat} />
                <span style={S.listenerEmptySeat} />
                <span style={S.listenerEmptySeat} />
              </div>
            )}
          </section>

          {isHost && speakRequests.length > 0 && (
            <div className="hide-scrollbar" style={S.hostPanel}>
              {speakRequests.map((r) => (
                <div key={r.fromSocketId} style={S.reqRow}>
                  <span style={S.reqName}>✋ {r.username}さんが話したいです</span>
                  <div style={S.reqActions}>
                    <button data-control="secondary" data-control-id="TalkVoiceRoom-approveBtn" type="button" style={S.approveBtn} onClick={() => approveRealSpeak(r.fromSocketId)}>発言を許可</button>
                    <button data-control="quiet" data-control-id="TalkVoiceRoom-rejectBtn" type="button" style={S.rejectBtn} onClick={() => rejectRealSpeak(r.fromSocketId)}>見送る</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          </div>
          <div style={S.toolbarWrap}>
            {!isHost && !canSpeak && (
              <button data-control="secondary" data-control-id="TalkVoiceRoom-requestSpeakBtn"
                type="button"
                style={S.requestSpeakBtn}
                onClick={requestRealSpeak}
                disabled={requestedSpeak || connecting}
              >
                <span style={S.btnRow}>
                  <IconHandRaised size={20} />
                  <span>{requestedSpeak ? 'ホストの承認待ち…' : '発言をリクエスト'}</span>
                </span>
              </button>
            )}
            {isHost && !canSpeak && (
              <button data-control="secondary" data-control-id="TalkVoiceRoom-requestSpeakBtn"
                type="button"
                style={S.requestSpeakBtn}
                onClick={resumeSpeakHost}
                disabled={connecting}
              >
                <span style={S.btnRow}>
                  <IconMic size={20} />
                  <span>ホストとして発言に戻る</span>
                </span>
              </button>
            )}
          </div>
          <div style={S.chatPanel}>
              <div ref={chatMessagesScrollRef} className="hide-scrollbar" style={S.chatMessages}>
                {chatMessages.length === 0 && (
                  <p style={S.chatEmpty}>まだメッセージはありません</p>
                )}
                {chatMessages.map((m, i) => {
                  const isMine = m.userId === user.id;
                  const canNav = Boolean(m.userId);
                  const canOpenProfile = canNav;
                  const openMsgProfile = () => {
                    if (canNav) openProfileInRoom(m.userId);
                  };
                  const avatarEl = (
                    <div
                      style={{
                        ...S.chatAvatar,
                        ...(isMine ? S.chatAvatarMine : {}),
                        ...(!canOpenProfile ? S.chatAvatarStatic : {}),
                      }}
                      onClick={() => canOpenProfile && openMsgProfile()}
                      role={canOpenProfile ? 'button' : undefined}
                      title={canOpenProfile ? `${m.username || '?'}のプロフィール` : undefined}
                    >
                      {(m.username || '?')[0].toUpperCase()}
                    </div>
                  );
                  return (
                    <div key={i} style={{ ...S.chatRow, ...(isMine ? S.chatRowMine : {}) }}>
                      {!isMine && avatarEl}
                      <div style={{ ...S.chatMsgWrap, ...(isMine ? S.chatMsgWrapMine : {}) }}>
                        <span style={{ ...S.chatMsgName, ...(isMine ? S.chatMsgNameMine : {}) }}>
                          {isMine ? 'あなた' : m.username}
                        </span>
                        <div style={{ ...S.chatBubble, ...(isMine ? S.chatBubbleMine : {}) }}>
                          {m.text}
                        </div>
                      </div>
                      {isMine && avatarEl}
                    </div>
                  );
                })}
              </div>
              <div style={S.chatInputRow}>
                <input
                  style={S.chatInput}
                  placeholder="メッセージを入力..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); sendChat(); } }}
                  maxLength={400}
                />
                <button data-control="icon" data-control-id="TalkVoiceRoom-chatToolMic"
                  type="button"
                  style={{
                    ...S.chatToolMic,
                    ...(!micOn ? S.chatToolMicOff : {}),
                    ...(!canSpeak ? S.chatToolMicDisabled : {}),
                  }}
                  onClick={toggleMic}
                  disabled={connecting || !canSpeak || !!error}
                  aria-pressed={canSpeak ? !micOn : undefined}
                  title={canSpeak ? (micOn ? 'マイクをオフ' : 'マイクをオン') : '聴講のみ'}
                >
                  {canSpeak ? (micOn ? <IconMic size={15} /> : <IconMicMuted size={15} />) : <IconHeadphones size={15} />}
                </button>
                <button data-control="primary" data-control-id="TalkVoiceRoom-chatSendBtn" aria-label="メッセージを送信" type="button" style={S.chatSendBtn} onClick={sendChat} disabled={!chatInput.trim()}>
                  送信
                </button>
              </div>
          </div>
        </div>
      )}
      {profileOverlay && (
        <ProfileOverlayModal
          userId={profileOverlay.userId}
          onClose={() => setProfileOverlay(null)}
        />
      )}
      {reportRoomOpen && customRoom && (
        <ReportModal
          targetType="room"
          targetId={customRoom.id}
          targetName={`${customRoom.title}（作成者: ${customRoom.owner_name}）`}
          // 通報の完了画面を読んでから閉じたタイミングで退室する
          onClose={() => {
            setReportRoomOpen(false);
            if (roomReportedRef.current) leave();
          }}
          onReported={() => { roomReportedRef.current = true; }}
        />
      )}
      {showPaywall && (
        <div style={S.paywallOverlay}>
          <div style={S.paywallCard}>
            <span style={S.paywallBadge}>✦</span>
            <p style={S.paywallTitle}>1時間の無料枠が終了しました</p>
            <p style={S.paywallDesc}>プレミアムプランで時間無制限に楽しめます。</p>
            {isIosNativeCheckout() ? (
              <IapSubscriptionDetails
                product={iapProduct}
                loading={!iapProduct && !iapLoadErr}
                error={iapLoadErr}
                onRetry={retryPaywallProduct}
                compact
              />
            ) : null}
            {paywallErr ? <p style={S.paywallErr}>{paywallErr}</p> : null}
            <button data-control="primary" data-control-id="TalkVoiceRoom-paywallBtn"
              type="button"
              style={{
                ...S.paywallBtn,
                ...(paywallBusy || (isIosNativeCheckout() && (!iapProduct || iapLoadErr)) ? S.paywallBtnDisabled : {}),
              }}
              onClick={handlePaywallPurchase}
              disabled={paywallBusy || (isIosNativeCheckout() && (!iapProduct || iapLoadErr))}
            >
              {paywallBusy ? '処理中…' : isIosNativeCheckout() ? 'App Storeで購入' : 'クレジットカードで支払う'}
            </button>
            <button data-control="quiet" data-control-id="TalkVoiceRoom-paywallLeave"
              type="button"
              style={S.paywallLeave}
              onClick={() => navigate('/talk', { replace: true })}
            >
              ルームを退出する
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const BG = '#07070f';
const GOLD = '#c9a84c';
const GOLD2 = '#e8d08a';
const GOLD_DIM = 'rgba(201,168,76,0.18)';
const GOLD_BORDER = 'rgba(201,168,76,0.22)';
const GLASS = 'rgba(255,255,255,0.035)';
const BORDER = 'rgba(201,168,76,0.15)';
const TEXT  = '#f0ece0';
const TEXT2 = 'rgba(240,236,224,0.55)';
const TEXT3 = 'rgba(240,236,224,0.32)';

const S = {
  page: {
    background: BG,
    // iOS のキーボード表示で 100vh は縮まず、ページ全体がスクロールして
    // ヘッダーと入力欄がズレる。画面に固定して土台自体を動かさない。
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    overscrollBehavior: 'none',
    color: TEXT,
    fontFamily: '"SF Pro Display", "Hiragino Sans", "Helvetica Neue", sans-serif',
  },

  /* ── Header ── */
  header: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 12px 10px 8px',
    background: 'linear-gradient(180deg, rgba(201,168,76,0.07) 0%, rgba(7,7,15,0.95) 100%)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderBottom: `1px solid ${GOLD_BORDER}`,
    flexShrink: 0,
    position: 'relative', zIndex: 10,
  },
  headerLeaveBtn: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '1px solid rgba(239,68,68,0.4)',
    background: 'rgba(239,68,68,0.12)',
    color: '#ff6b6b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    padding: 0,
    margin: 0,
    fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: '0 0 12px rgba(239,68,68,0.15)',
  },
  // ── 通報・安全メニュー（Guideline 1.2） ──
  safetyWrap: { position: 'relative', flexShrink: 0 },
  headerSafetyBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '1px solid rgba(201,168,76,0.28)',
    background: 'rgba(201,168,76,0.10)',
    color: 'rgba(232,208,138,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    marginRight: 8,
    WebkitTapHighlightColor: 'transparent',
  },
  safetyBackdrop: { position: 'fixed', inset: 0, zIndex: 300 },
  safetyMenu: {
    position: 'absolute',
    top: 44,
    right: 0,
    zIndex: 301,
    width: 250,
    padding: 8,
    background: '#18160f',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 16,
    boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
    animation: 'fadeInUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
  },
  safetyMenuItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '11px 12px',
    borderRadius: 10,
    background: 'rgba(201,168,76,0.10)',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13.5,
    fontWeight: 800,
    color: '#f0ece0',
  },
  safetyMenuHint: {
    margin: '8px 4px 2px',
    fontSize: 11,
    lineHeight: 1.6,
    color: 'rgba(240,236,224,0.5)',
  },
  headerMid: { flex: 1, minWidth: 0 },
  title: {
    fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', margin: 0,
    background: `linear-gradient(135deg, ${GOLD2}, ${GOLD})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  roomId: { fontSize: 11, color: TEXT3, fontWeight: 500 },
  headerSpacer: { width: 40 },

  /* ── Status ── */
  status: { padding: '10px 16px 8px' },
  statusTxt: {
    margin: 0, fontSize: 13, fontWeight: 500,
    color: TEXT2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
  },
  statusLabel: { fontSize: 12, fontWeight: 600, color: TEXT3 },
  liveDot: {
    width: 8, height: 8, borderRadius: '50%',
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
    flexShrink: 0,
    boxShadow: `0 0 0 3px rgba(201,168,76,0.28), 0 0 8px rgba(201,168,76,0.5)`,
  },
  liveDotDemo: {
    width: 8, height: 8, borderRadius: '50%',
    background: 'rgba(160,120,200,0.9)', flexShrink: 0,
    boxShadow: '0 0 0 3px rgba(160,120,200,0.2)',
  },
  statusMuted: {
    margin: '8px 0 0', padding: '10px 14px', borderRadius: 12,
    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.22)',
    fontSize: 13, fontWeight: 500, color: '#ff8080', lineHeight: 1.45,
    display: 'flex', alignItems: 'flex-start', gap: 10,
  },
  statusMutedWarn: {
    background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.5)',
    color: '#ff5555', fontWeight: 700,
  },
  statusMutedIcon: { flexShrink: 0, display: 'flex', color: '#ff6b6b', marginTop: 1 },

  /* ── Demo banner ── */
  demoBanner: {
    margin: 0, padding: '10px 16px',
    background: 'rgba(160,120,200,0.06)', borderBottom: `1px solid rgba(160,120,200,0.2)`,
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
  },
  demoPill: {
    fontSize: 11, fontWeight: 700, color: 'rgba(200,160,240,0.8)',
    background: 'rgba(160,120,200,0.12)', border: '1px solid rgba(160,120,200,0.25)',
    padding: '3px 10px', borderRadius: 100, flexShrink: 0,
  },
  demoBannerText: { fontSize: 13, fontWeight: 400, color: TEXT3, lineHeight: 1.4, flex: 1, minWidth: 0 },

  /* ── Layout ── */
  roomBody: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
  // 上下を固定し、スクロールするのはここだけ
  participantStrip: {
    flex: 1, minHeight: 0, overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column',
    paddingBottom: 12,
  },
  speakerStage: {
    margin: '8px 12px 0',
    padding: '12px 12px 16px',
    borderRadius: 22,
    background: 'radial-gradient(circle at 50% 12%, rgba(201,168,76,0.22), transparent 42%), linear-gradient(180deg, rgba(201,168,76,0.1), rgba(255,255,255,0.028))',
    border: `1px solid ${GOLD_BORDER}`,
    boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
    position: 'relative',
    overflow: 'hidden',
  },
  stageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 30,
    padding: '0 2px 12px',
  },
  stageGlow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    flex: 1,
  },
  stageOrb: {
    width: 34,
    height: 10,
    borderRadius: 9999,
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
    opacity: 0.85,
    boxShadow: '0 0 22px rgba(201,168,76,0.42)',
  },
  stageOrbSmall: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: 'rgba(232,208,138,0.45)',
    boxShadow: '0 0 14px rgba(232,208,138,0.25)',
  },
  stageStats: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  stageStatPill: {
    padding: '5px 9px',
    borderRadius: 100,
    background: 'rgba(7,7,15,0.42)',
    border: `1px solid ${BORDER}`,
    color: GOLD2,
    fontSize: 11,
    fontWeight: 800,
  },

  /* ── Speaker grid: responsive wrap, all visible without horizontal scroll ── */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(86px, 1fr))',
    gap: 12,
    padding: 0,
    alignItems: 'start',
  },
  tile: {
    minHeight: 106,
    padding: '7px 4px 6px',
    background: 'transparent',
    border: 'none',
    borderRadius: 18,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    boxShadow: 'none',
    transition: 'transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), border-color 0.18s ease, box-shadow 0.18s ease',
  },
  tileHost: {
    transform: 'translateY(-2px)',
  },
  tileSelf: {
    borderRadius: 18,
  },
  tileSpeaking: {
    transform: 'translateY(-4px)',
  },
  emptySeatRow: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
    padding: '12px 0 8px',
  },
  emptySeat: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    border: `1px dashed rgba(201,168,76,0.22)`,
    background: 'rgba(255,255,255,0.025)',
  },
  emptySeatMain: {
    width: 68,
    height: 68,
    borderColor: 'rgba(201,168,76,0.34)',
    background: 'rgba(201,168,76,0.05)',
  },
  tileAvatarWrap: { position: 'relative', width: 64, height: 64, flexShrink: 0 },
  tileAvatarBtn: {
    border: 'none', padding: 0, margin: 0, background: 'none',
    borderRadius: '50%', cursor: 'pointer', display: 'flex', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  micOffOverlay: {
    position: 'absolute', right: -1, bottom: -1, display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ff6b6b', background: '#1a0a0a',
    borderRadius: '50%', padding: 3,
    boxShadow: '0 1px 4px rgba(0,0,0,0.4)', pointerEvents: 'none',
  },
  tileAvatar: {
    width: 64, height: 64, borderRadius: '50%',
    background: `linear-gradient(135deg, rgba(201,168,76,0.28), rgba(232,208,138,0.12))`,
    border: `2px solid rgba(201,168,76,0.34)`,
    color: GOLD2,
    fontWeight: 900, fontSize: 23, letterSpacing: '-0.02em',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 10px 24px rgba(0,0,0,0.28)',
  },
  tileName: {
    marginTop: 7,
    fontSize: 11, fontWeight: 800, color: TEXT,
    textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.2, maxWidth: '100%',
  },
  roleRow: { display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', minHeight: 18 },
  badgeHost: {
    width: 19,
    height: 19,
    borderRadius: '50%',
    fontSize: 11,
    fontWeight: 900,
    color: '#1a1000',
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 8px rgba(201,168,76,0.28)`,
  },
  badgeSpeaking: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    background: '#34d399',
    boxShadow: '0 0 0 4px rgba(52,211,153,0.14), 0 0 14px rgba(52,211,153,0.45)',
  },
  badgeMicOff: {
    width: 19,
    height: 19,
    borderRadius: '50%',
    color: '#ff8080',
    background: 'rgba(239,68,68,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* ── Listener deck ── */
  listenerDeck: {
    margin: '10px 12px 0',
    padding: '10px 10px 11px',
    borderRadius: 18,
    background: 'rgba(255,255,255,0.022)',
    border: `1px solid rgba(255,255,255,0.06)`,
  },
  listenerHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 9,
  },
  listenerWave: {
    height: 7,
    flex: 1,
    borderRadius: 9999,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.04), rgba(201,168,76,0.22), rgba(255,255,255,0.04))',
  },
  listenerCount: {
    fontSize: 11,
    fontWeight: 800,
    color: TEXT2,
    padding: '4px 9px',
    borderRadius: 100,
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid rgba(255,255,255,0.06)`,
    flexShrink: 0,
  },
  listenerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))',
    gap: 7,
  },
  listenerChip: {
    minWidth: 0,
    padding: '5px 3px',
    borderRadius: 14,
    background: 'transparent',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    color: 'inherit',
    fontFamily: 'inherit',
  },
  listenerChipBtn: {
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  listenerAvatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    border: `1px solid rgba(255,255,255,0.08)`,
    color: TEXT2,
    fontSize: 13,
    fontWeight: 900,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  listenerAvatarSelf: {
    background: GOLD_DIM,
    color: GOLD2,
    border: `1px solid rgba(201,168,76,0.32)`,
    boxShadow: '0 0 0 2px rgba(201,168,76,0.12)',
  },
  listenerName: {
    maxWidth: '100%',
    fontSize: 9,
    lineHeight: 1.15,
    fontWeight: 700,
    color: TEXT2,
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  listenerSelfPill: {
    fontSize: 8,
    fontWeight: 900,
    color: GOLD,
    lineHeight: 1,
  },
  listenerEmptySeats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 8,
    padding: '2px 4px 0',
  },
  listenerEmptySeat: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    justifySelf: 'center',
    border: '1px dashed rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.025)',
  },

  /* ── Host request panel ── */
  hostPanel: {
    margin: '0 16px 10px', padding: '14px 16px', borderRadius: 14,
    border: `1px solid ${GOLD_BORDER}`, background: GLASS,
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    maxHeight: 200, overflowY: 'auto', overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    boxShadow: `0 0 20px rgba(201,168,76,0.08)`,
  },
  hostPanelTitle: {
    margin: '0 0 10px', fontSize: 10, fontWeight: 700,
    color: GOLD, textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  reqRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 10, padding: '11px 0', borderTop: `1px solid ${BORDER}`,
  },
  reqName: { fontSize: 14, fontWeight: 500, color: TEXT, flex: 1, minWidth: 0 },
  reqActions: { display: 'flex', gap: 8, flexShrink: 0 },
  approveBtn: {
    padding: '7px 16px', borderRadius: 100, border: 'none',
    background: `linear-gradient(135deg, ${GOLD}, #a07830)`,
    color: '#1a1000',
    fontSize: 12, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer',
    boxShadow: `0 2px 10px rgba(201,168,76,0.3)`,
  },
  rejectBtn: {
    padding: '7px 16px', borderRadius: 100,
    border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.04)',
    color: TEXT2, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  },

  /* ── Toolbar wrap ── */
  toolbarWrap: {
    flexShrink: 0,
    padding: '0 0 env(safe-area-inset-bottom)',
    display: 'flex', flexDirection: 'column', alignItems: 'stretch',
    background: 'linear-gradient(0deg, rgba(7,7,15,0.98) 0%, rgba(10,8,18,0.95) 100%)',
    backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
    borderTop: `1px solid ${GOLD_BORDER}`,
    boxShadow: `0 -4px 24px rgba(0,0,0,0.5)`,
  },
  btnRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },
  requestSpeakBtn: {
    margin: '12px 16px 0', padding: '13px 18px', borderRadius: 14,
    border: `1px solid ${GOLD_BORDER}`,
    background: GOLD_DIM,
    color: GOLD2, fontSize: 15, fontWeight: 700,
    fontFamily: 'inherit', cursor: 'pointer',
    boxShadow: `0 0 16px rgba(201,168,76,0.12)`,
  },
  // スクロール領域の外。画面下部に固定される帯
  chatPanel: {
    borderTop: `1px solid ${BORDER}`,
    background: 'linear-gradient(180deg, rgba(7,7,15,0.86) 0%, rgba(7,7,15,0.97) 100%)',
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    display: 'flex', flexDirection: 'column', flexShrink: 0,
  },
  chatMessages: {
    minHeight: 80, maxHeight: 'min(160px, 26vh)',
    overflowY: 'auto', overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    padding: '10px 14px 6px', display: 'flex', flexDirection: 'column', gap: 8,
  },
  chatEmpty: { textAlign: 'center', fontSize: 13, color: TEXT3, padding: '16px 0', margin: 0 },
  chatRow: { display: 'flex', alignItems: 'flex-end', gap: 8 },
  chatRowMine: { justifyContent: 'flex-end' },
  chatAvatar: {
    width: 26, height: 26, borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)', color: TEXT2,
    fontSize: 10, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, cursor: 'pointer', userSelect: 'none',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  chatAvatarMine: {
    background: GOLD_DIM, color: GOLD,
    border: `1px solid rgba(201,168,76,0.3)`,
  },
  chatAvatarStatic: { cursor: 'default', pointerEvents: 'none' },
  chatMsgWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, maxWidth: '75%' },
  chatMsgWrapMine: { alignItems: 'flex-end' },
  chatMsgName: { fontSize: 10, fontWeight: 600, color: TEXT3, paddingLeft: 4 },
  chatMsgNameMine: { paddingLeft: 0, paddingRight: 4, color: GOLD },
  chatBubble: {
    background: 'rgba(255,255,255,0.085)', border: `1px solid ${BORDER}`,
    borderRadius: '14px 14px 14px 4px', padding: '8px 12px',
    fontSize: 13, color: TEXT, lineHeight: 1.5, wordBreak: 'break-word',
    // 吹き出しごとの backdrop-filter はスクロールのたびに再合成が走り、
    // 件数が増えるほど重くなる。背景を少し濃くして代替する。
  },
  chatBubbleMine: {
    background: `linear-gradient(135deg, ${GOLD}, #a07830)`,
    border: 'none',
    borderRadius: '14px 14px 4px 14px',
    color: '#1a1000',
    fontWeight: 600,
    boxShadow: `0 2px 12px rgba(201,168,76,0.35)`,
  },
  chatInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px calc(80px + env(safe-area-inset-bottom))',
    borderTop: `1px solid ${BORDER}`,
  },
  chatInput: {
    flex: 1,
    minWidth: 0,
    padding: '7px 12px',
    borderRadius: 20,
    border: `1px solid ${BORDER}`,
    fontSize: 13,
    lineHeight: 1.3,
    outline: 'none',
    background: 'rgba(255,255,255,0.05)',
    color: TEXT,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  chatToolMic: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    border: 'none',
    background: `linear-gradient(135deg, ${GOLD}, #a07830)`,
    color: '#1a1000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    boxShadow: `0 2px 10px rgba(201,168,76,0.35)`,
    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
    fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
  },
  chatToolMicOff: {
    background: 'linear-gradient(135deg, #dc2626, #991b1b)',
    boxShadow: '0 2px 10px rgba(239,68,68,0.4)',
    color: '#fff',
  },
  chatToolMicDisabled: { opacity: 0.3, cursor: 'not-allowed', pointerEvents: 'none', boxShadow: 'none' },
  chatSendBtn: {
    padding: '6px 14px',
    borderRadius: 20,
    border: 'none',
    background: `linear-gradient(135deg, ${GOLD}, #a07830)`,
    color: '#1a1000',
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
    fontFamily: 'inherit',
    cursor: 'pointer',
    flexShrink: 0,
    boxShadow: `0 2px 8px rgba(201,168,76,0.25)`,
  },
  paywallOverlay: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(7,7,15,0.85)',
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 'max(16px, env(safe-area-inset-top)) 20px max(16px, env(safe-area-inset-bottom))',
  },
  paywallCard: {
    width: '100%', maxWidth: 360,
    background: 'rgba(20,16,36,0.98)',
    border: `1px solid rgba(201,168,76,0.35)`,
    borderRadius: 20, padding: '28px 24px 24px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 10, textAlign: 'center',
    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
  },
  paywallBadge: {
    fontSize: 28,
    background: `linear-gradient(135deg, ${GOLD2}, ${GOLD})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  paywallTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: TEXT },
  paywallDesc: { margin: 0, fontSize: 13, color: TEXT2, lineHeight: 1.6 },
  paywallPrice: { margin: 0, fontSize: 26, fontWeight: 800, color: TEXT },
  paywallPer: { fontSize: 13, fontWeight: 600, color: TEXT3 },
  paywallErr: { margin: 0, fontSize: 12, color: '#f88' },
  paywallBtn: {
    marginTop: 6, width: '100%', padding: '15px 16px',
    borderRadius: 14, border: 'none', cursor: 'pointer',
    fontSize: 16, fontWeight: 800, fontFamily: 'inherit',
    background: `linear-gradient(135deg, ${GOLD}, #a07830)`,
    color: '#1a1000', boxShadow: '0 4px 20px rgba(201,168,76,0.35)',
  },
  paywallBtnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  paywallLeave: {
    width: '100%', padding: '13px 16px',
    borderRadius: 14, border: `1px solid rgba(255,255,255,0.1)`,
    cursor: 'pointer', fontSize: 14, fontWeight: 600,
    fontFamily: 'inherit', color: TEXT2, background: 'transparent',
  },
  errorBox: { padding: 32, textAlign: 'center' },
  errorText: { color: '#ff8080', fontWeight: 600, marginBottom: 20 },
  leaveBtn: {
    padding: '12px 24px', borderRadius: 14, border: 'none',
    background: `linear-gradient(135deg, ${GOLD}, #a07830)`,
    color: '#1a1000',
    fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
    boxShadow: `0 4px 16px rgba(201,168,76,0.3)`,
  },
};
